import { useEffect, useRef } from "react";
import * as monaco from "monaco-editor";
import { ensureMonacoEnvironment } from "@/lib/monaco-env";
import { pineEditorOptions, pineEditorTheme, registerPineLanguage } from "@/lib/pine-monarch";
import { useTheme } from "@/hooks/useTheme";
import "monaco-editor/min/vs/editor/editor.main.css";

ensureMonacoEnvironment();
registerPineLanguage(monaco);

export function ScriptEditor({
  value,
  onChange,
  onSave,
  height = 280,
  maxHeight,
  readOnly = false,
}: {
  value: string;
  onChange?: (value: string) => void;
  /** Ctrl/Cmd+S while the editor is focused. */
  onSave?: () => void;
  /** Pixel height, or any CSS length (e.g. `"100%"`). */
  height?: number | string;
  /** When set, editor sizes to content up to this CSS max-height. */
  maxHeight?: string | number;
  readOnly?: boolean;
}) {
  const { theme } = useTheme();
  const hostRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const onChangeRef = useRef(onChange);
  const onSaveRef = useRef(onSave);
  const focusedRef = useRef(false);
  const notifyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  onChangeRef.current = onChange;
  onSaveRef.current = onSave;

  const heightCss = maxHeight
    ? typeof maxHeight === "number"
      ? `${maxHeight}px`
      : maxHeight
    : typeof height === "number"
      ? `${height}px`
      : height;

  useEffect(() => {
    if (!hostRef.current) return;

    const editor = monaco.editor.create(
      hostRef.current,
      pineEditorOptions({
        value,
        readOnly,
        theme: pineEditorTheme(theme),
        scrollbar: {
          verticalScrollbarSize: 10,
          horizontalScrollbarSize: 10,
        },
        // Typing feels smoother without expensive rulers/minimap work.
        renderLineHighlight: "line",
        occurrencesHighlight: "off",
        renderValidationDecorations: "off",
      })
    );
    editorRef.current = editor;

    const focusDisposable = editor.onDidFocusEditorWidget(() => {
      focusedRef.current = true;
    });
    const blurDisposable = editor.onDidBlurEditorWidget(() => {
      focusedRef.current = false;
      // Flush pending text to React on blur so Save always sees the latest.
      if (notifyTimerRef.current) {
        clearTimeout(notifyTimerRef.current);
        notifyTimerRef.current = null;
      }
      onChangeRef.current?.(editor.getValue());
    });
    // Debounce React updates — pushing every keystroke re-renders the whole page.
    const changeDisposable = editor.onDidChangeModelContent(() => {
      if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current);
      notifyTimerRef.current = setTimeout(() => {
        notifyTimerRef.current = null;
        onChangeRef.current?.(editor.getValue());
      }, 120);
    });

    editor.addAction({
      id: "pine-save-script",
      label: "Save Script",
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS],
      run: () => {
        if (notifyTimerRef.current) {
          clearTimeout(notifyTimerRef.current);
          notifyTimerRef.current = null;
        }
        onChangeRef.current?.(editor.getValue());
        onSaveRef.current?.();
      },
    });

    return () => {
      if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current);
      focusDisposable.dispose();
      blurDisposable.dispose();
      changeDisposable.dispose();
      editor.dispose();
      editorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  useEffect(() => {
    monaco.editor.setTheme(pineEditorTheme(theme));
  }, [theme]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || focusedRef.current) return;
    if (editor.getValue() !== value) {
      editor.setValue(value);
    }
  }, [value]);

  // Layout only when size props change — not on every keystroke.
  useEffect(() => {
    const editor = editorRef.current;
    const host = hostRef.current;
    if (!editor || !host) return;

    if (maxHeight) {
      const lineCount = Math.max(editor.getModel()?.getLineCount() ?? 1, 1);
      const lineHeight = editor.getOption(monaco.editor.EditorOption.lineHeight);
      const padding = 16;
      const contentH = lineCount * lineHeight + padding;
      const maxPx =
        typeof maxHeight === "number"
          ? maxHeight
          : Number.parseInt(String(maxHeight), 10) || 480;
      host.style.height = `${Math.min(contentH, maxPx)}px`;
    } else {
      host.style.height = heightCss;
    }
    editor.layout();
  }, [height, maxHeight, heightCss]);

  return (
    <div
      ref={hostRef}
      className="w-full min-w-0 max-w-full overflow-hidden rounded-[var(--radius-input)] border border-[var(--input-border)]"
      style={{ height: heightCss }}
    />
  );
}
